'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface GuideQuestion {
  id: string;
  question: string;
  hint: string;
  options: { emoji: string; label: string; sublabel: string; value: string }[];
}

interface BMSRecommendation {
  name: string;
  type: string;
  current: string;
  balancing: string;
  comm: string;
  protection: string;
  price: string;
  best: string;
}

const guideQuestions: GuideQuestion[] = [
  {
    id: 'chemistry',
    question: 'What battery chemistry are you using?',
    hint: 'Different chemistries require different voltage cutoffs and balancing algorithms',
    options: [
      { emoji: '🔋', label: 'LiFePO₄ (LFP)', sublabel: '3.2V nominal, safest chemistry', value: 'lfp' },
      { emoji: '⚡', label: 'NMC / NCA', sublabel: '3.7V nominal, higher energy density', value: 'nmc' },
      { emoji: '🚀', label: 'LTO (Titanate)', sublabel: '2.4V nominal, fastest charging', value: 'lto' },
      { emoji: '❓', label: 'Not sure yet', sublabel: 'Help me decide', value: 'unsure' },
    ],
  },
  {
    id: 'cells',
    question: 'How many cells in series (S)?',
    hint: 'This determines the pack voltage. More cells = higher voltage',
    options: [
      { emoji: '🔹', label: '4S (12.8V)', sublabel: 'UPS / Inverter replacement', value: '4s' },
      { emoji: '🔸', label: '8S (25.6V)', sublabel: 'Small solar / inverter', value: '8s' },
      { emoji: '🔷', label: '16S (51.2V)', sublabel: 'Industrial / commercial', value: '16s' },
      { emoji: '🔶', label: '24S+ (77V+)', sublabel: 'High-voltage systems', value: '24s' },
    ],
  },
  {
    id: 'current',
    question: 'What is your peak discharge current?',
    hint: 'Always choose BMS with 20% higher current rating than your peak requirement',
    options: [
      { emoji: '🐢', label: 'Up to 50A', sublabel: 'Small UPS / home inverter', value: '50a' },
      { emoji: '🐊', label: '50A – 100A', sublabel: 'Office / small commercial', value: '100a' },
      { emoji: '🦊', label: '100A – 200A', sublabel: 'Industrial / large commercial', value: '200a' },
      { emoji: '🦖', label: '200A+', sublabel: 'Heavy industrial / utility', value: '200aplus' },
    ],
  },
  {
    id: 'balancing',
    question: 'What type of cell balancing do you need?',
    hint: 'Active balancing transfers energy efficiently. Passive dissipates as heat but costs less.',
    options: [
      { emoji: '⚖️', label: 'Passive Balancing', sublabel: 'Cost-effective, standard packs', value: 'passive' },
      { emoji: '⚡', label: 'Active Balancing', sublabel: 'High efficiency, premium packs', value: 'active' },
      { emoji: '🤔', label: 'Not sure', sublabel: 'Recommend for me', value: 'unsure' },
    ],
  },
  {
    id: 'communication',
    question: 'What communication protocol does your inverter use?',
    hint: 'BMS must communicate with your inverter or controller for proper operation',
    options: [
      { emoji: '📶', label: 'CAN Bus', sublabel: 'Most inverters & EV systems', value: 'can' },
      { emoji: '💻', label: 'RS485 / Modbus', sublabel: 'Industrial inverters & PLCs', value: 'rs485' },
      { emoji: '📱', label: 'Bluetooth / Wi-Fi', sublabel: 'IoT & app-connected systems', value: 'bt' },
      { emoji: '❓', label: 'Not sure', sublabel: 'I need guidance', value: 'unsure' },
    ],
  },
  {
    id: 'application',
    question: 'What is your primary application?',
    hint: 'Application determines protection priorities and operating parameters',
    options: [
      { emoji: '🏢', label: 'UPS / Inverter Backup', sublabel: 'Office & commercial backup', value: 'ups' },
      { emoji: '☀️', label: 'Solar Storage', sublabel: 'Off-grid & hybrid solar', value: 'solar' },
      { emoji: '🏭', label: 'Industrial / BESS', sublabel: 'Factory & large commercial', value: 'industrial' },
      { emoji: '🚗', label: 'Electric Vehicle', sublabel: 'EV / e-rickshaw / e-bike', value: 'ev' },
    ],
  },
];

const bmsRecommendations: Record<string, BMSRecommendation> = {
  default: {
    name: 'AGLS BMS-100A Smart',
    type: 'Smart BMS with Bluetooth',
    current: '100A continuous',
    balancing: 'Passive (60mA)',
    comm: 'RS485 + Bluetooth',
    protection: 'OVP, UVP, OCP, OTP, SCP',
    price: 'Contact for pricing',
    best: 'UPS, inverter backup, small solar',
  },
  industrial: {
    name: 'AGLS BMS-200A Industrial',
    type: 'Industrial BMS with CAN',
    current: '200A continuous / 400A peak',
    balancing: 'Active (2A)',
    comm: 'CAN 2.0 + RS485 + Modbus',
    protection: 'OVP, UVP, OCP, OTP, SCP, BVP',
    price: 'Contact for pricing',
    best: 'Industrial BESS, large commercial, solar farms',
  },
  ev: {
    name: 'AGLS BMS-EV Pro',
    type: 'Automotive-grade BMS',
    current: '300A continuous',
    balancing: 'Active (3A)',
    comm: 'CAN 2.0B + Bluetooth',
    protection: 'OVP, UVP, OCP, OTP, SCP + Crash detection',
    price: 'Contact for pricing',
    best: 'Electric vehicles, e-rickshaws, e-bikes',
  },
};

const bmsServices = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'BMS Configuration',
    desc: 'Complete parameter tuning for protection thresholds, charge profiles, and balancing settings matched to your cell chemistry and pack design.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    title: 'Custom Firmware',
    desc: 'Tailored BMS firmware optimized for your specific cell chemistry, pack configuration, and operating conditions. SOC and SOH algorithms calibrated for accuracy.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Safety & Compliance',
    desc: 'Software designed to meet industrial safety standards with comprehensive fault handling and multi-layer protection.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
      </svg>
    ),
    title: 'Hardware BMS Selection',
    desc: 'We source and configure the right BMS hardware for your application. From passive balancing for small packs to active balancing for industrial systems.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Integration & Testing',
    desc: 'Complete BMS integration with your inverter or controller. Communication protocol setup (CAN, RS485, Modbus) and full system validation.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    title: 'Remote Monitoring',
    desc: 'Cloud-based BMS dashboards for remote diagnostics, performance tracking, and predictive maintenance alerts across your fleet.',
  },
];

export default function BmsSoftwarePage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll');
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }); },
      { threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < guideQuestions.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResult(false);
  };

  const getRecommendation = (): BMSRecommendation => {
    if (answers.application === 'ev') return bmsRecommendations.ev;
    if (answers.application === 'industrial' || answers.current === '200aplus') return bmsRecommendations.industrial;
    return bmsRecommendations.default;
  };

  const currentQuestion = guideQuestions[currentStep];
  const progress = ((currentStep + 1) / guideQuestions.length) * 100;
  const currentAnswer = answers[currentQuestion?.id];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 circuit-bg opacity-30" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="section-label mb-6">BMS Solutions</div>
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            The Brain Inside
            <br />
            <span className="gradient-text-green">Every Battery Pack</span>
          </h1>
          <p className="text-slate-600 text-lg max-w-3xl leading-relaxed mb-8">
            Custom BMS firmware, configuration, and integration services for energy storage systems.
            We build the intelligence that protects your cells and maximizes performance.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link href="/contact" className="btn-primary text-base px-8 py-3">
              Discuss Your BMS Needs
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link href="/batteries" className="btn-secondary text-base px-8 py-3">
              Battery Pack Builder
            </Link>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Services</div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              What We <span className="gradient-text-green">Offer</span>
            </h2>
            <p className="text-slate-600">End-to-end BMS solutions from hardware selection to firmware development and field deployment.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {bmsServices.map((s, i) => (
              <div key={i} className="card-dark p-6 animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4">
                  {s.icon}
                </div>
                <h3 className="text-slate-900 font-bold mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How BMS Works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              How a BMS <span className="gradient-text-green">Works</span>
            </h2>
            <p className="text-slate-600">The BMS monitors every cell, manages charge/discharge, and communicates with your inverter in real time.</p>
          </div>

          {/* Architecture diagram */}
          <div className="card-dark p-8 animate-on-scroll">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Inputs */}
              <div className="space-y-3">
                <h4 className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-4">Inputs / Sensors</h4>
                {[
                  { label: 'BATTERY CELLS', sub: 'Series / Parallel Stack', icon: '🔋' },
                  { label: 'VOLTAGE SENSING', sub: 'Per-cell monitoring', icon: '📊' },
                  { label: 'CURRENT SENSOR', sub: 'Hall effect / Shunt', icon: '⚡' },
                  { label: 'TEMPERATURE SENSORS', sub: 'NTC thermistors', icon: '🌡️' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-200">
                    <span className="text-xl">{item.icon}</span>
                    <div>
                      <div className="text-slate-900 text-xs font-semibold">{item.label}</div>
                      <div className="text-slate-500 text-xs">{item.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* BMS Core */}
              <div className="flex flex-col items-center justify-center">
                <div className="w-full bg-primary/10 border-2 border-primary/40 rounded-2xl p-6 text-center neon-border">
                  <div className="text-primary font-bold text-lg mb-3">BMS MCU</div>
                  <div className="space-y-2">
                    {['SOC Estimation', 'Cell Balancing', 'Protection Logic', 'Fault Detection', 'Communication'].map((f) => (
                      <div key={f} className="text-xs text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-1.5">{f}</div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Outputs */}
              <div className="space-y-3">
                <h4 className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-4">Outputs / Control</h4>
                {[
                  { label: 'COMMUNICATION', sub: 'CAN · RS485 · Modbus · BT', icon: '📶' },
                  { label: 'PROTECTION', sub: 'MOSFET Switching · OVP · UVP', icon: '🛡️' },
                  { label: 'CELL BALANCING', sub: 'Active / Passive', icon: '⚖️' },
                  { label: 'DISPLAY / APP', sub: 'LCD · Bluetooth · Cloud', icon: '📱' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-200">
                    <span className="text-xl">{item.icon}</span>
                    <div>
                      <div className="text-slate-900 text-xs font-semibold">{item.label}</div>
                      <div className="text-slate-500 text-xs">{item.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-center text-slate-500 text-xs">Battery Management System — Functional Architecture</p>
          </div>
        </div>
      </section>

      {/* BMS Selection Guide */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" id="selection-guide">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Interactive Tool</div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              BMS <span className="gradient-text-green">Selection Guide</span>
            </h2>
            <p className="text-slate-600">
              Answer a few questions about your battery configuration and we&apos;ll recommend the ideal BMS for your needs.
            </p>
          </div>

          <div className="card-dark p-8">
            {!showResult ? (
              <>
                {/* Progress */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-500 text-sm">Question {currentStep + 1} of {guideQuestions.length}</span>
                    <span className="text-primary text-sm font-semibold">{Math.round(progress)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                {/* Step dots */}
                <div className="flex items-center gap-2 mb-8">
                  {guideQuestions.map((_, i) => (
                    <React.Fragment key={i}>
                      <div
                        className={`step-dot ${
                          i < currentStep ? 'completed' : i === currentStep ? 'active' : 'pending'
                        }`}
                      >
                        {i < currentStep ? (
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          i + 1
                        )}
                      </div>
                      {i < guideQuestions.length - 1 && (
                        <div className={`step-line ${i < currentStep ? 'completed' : ''}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Question */}
                <div className="mb-6">
                  <h3 className="text-slate-900 font-bold text-xl mb-2">{currentQuestion.question}</h3>
                  <p className="text-slate-500 text-sm">{currentQuestion.hint}</p>
                </div>

                {/* Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {currentQuestion.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(currentQuestion.id, opt.value)}
                      className={`selection-card text-left ${currentAnswer === opt.value ? 'selected' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{opt.emoji}</span>
                        <div>
                          <div className="text-slate-900 font-semibold text-sm">{opt.label}</div>
                          <div className="text-slate-500 text-xs mt-0.5">{opt.sublabel}</div>
                        </div>
                        {currentAnswer === opt.value && (
                          <div className="ml-auto">
                            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                              <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    className="btn-secondary disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                    </svg>
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!currentAnswer}
                    className="btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {currentStep === guideQuestions.length - 1 ? 'Get Recommendation' : 'Next'}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              /* Result */
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-slate-900 font-bold text-2xl mb-2">Your BMS Recommendation</h3>
                  <p className="text-slate-600 text-sm">Based on your requirements, we recommend:</p>
                </div>

                {(() => {
                  const rec = getRecommendation();
                  return (
                    <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-primary font-bold text-xl">{rec.name}</h4>
                          <p className="text-slate-500 text-sm">{rec.type}</p>
                        </div>
                        <span className="badge badge-green">Recommended</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { label: 'Current Rating', value: rec.current },
                          { label: 'Cell Balancing', value: rec.balancing },
                          { label: 'Communication', value: rec.comm },
                          { label: 'Protection', value: rec.protection },
                          { label: 'Best For', value: rec.best },
                          { label: 'Pricing', value: rec.price },
                        ].map((item) => (
                          <div key={item.label} className="spec-row">
                            <span className="text-slate-500 text-sm">{item.label}</span>
                            <span className="text-slate-900 text-sm font-medium">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="flex flex-wrap gap-3 justify-center">
                  <Link href="/contact" className="btn-primary">
                    Get a Quote for This BMS
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <button onClick={handleReset} className="btn-secondary">
                    Start Over
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: '⚖️', title: 'Active vs Passive', desc: 'Active balancing transfers energy efficiently. Passive dissipates as heat but costs less.' },
              { icon: '⚡', title: 'Current Rating', desc: 'Always choose BMS with 20% higher current rating than your peak discharge requirement.' },
              { icon: '🛡️', title: 'Safety First', desc: 'Quality BMS protects against overcharge, over-discharge, short circuit, and overtemperature.' },
            ].map((tip, i) => (
              <div key={i} className="card-dark p-6 animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-3xl mb-3">{tip.icon}</div>
                <h3 className="text-slate-900 font-bold mb-2">{tip.title}</h3>
                <p className="text-slate-600 text-sm">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 text-center animate-on-scroll">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Design Your Custom Battery Pack</h2>
          <p className="text-slate-600 mb-8">
            Use our Battery Pack Builder to select cells, configure BMS, and get instant pricing for your project.
          </p>
          <Link href="/batteries" className="btn-primary text-base px-10 py-4">
            Open Battery Pack Builder
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
